<template>
  <table class="table table-list mt-3">
    <slot name="thead"></slot>
    <tbody>
      <tr v-for="(date, index) in dates" :key="date">
        <td class="text-center fit">{{ date }}</td>
        <td class="text-center fit" v-if="!isMonthly">{{ data[date].week }}</td>
        <td>{{ data[date]['prevYear']['result'] }}</td>
        <td>
          <input v-if="isEditable" form="editPerformanceForm" type="number" class="form-control form-control-sm" :name="'performances[' + date + '][goal]'"
            v-model.number="performances[date].goal" min="0" step="0.01">
          <span v-else>{{ performances[date].goal }}</span>
        </td>
        <td>
          <input v-if="isEditable" form="editPerformanceForm" type="number" class="form-control form-control-sm" :name="'performances[' + date + '][result]'"
            v-model.number="performances[date].result" min="0" step="0.01">
          <span v-else>{{ performances[date].result }}</span>
        </td>
        <td class="text-center">{{ formatPercent(prevYearComparison(date)) }}</td>
        <td class="text-center">{{ formatPercent(thisYearComparison(date)) }}</td>
        <td class="text-center">{{ formatDecimal(cumulativeTotal[index].result) }}</td>
        <td class="text-center">{{ formatPercent(cumulativeTotalComparison(index)) }}</td>
        <td v-if="isMonthly" class="text-center">
          <i v-if="isEditable" class="fas fa-sync-alt" style="cursor: pointer;" @click="reflectDailyPerformances(date)"></i>
        </td>
      </tr>
    </tbody>
    <tfoot class="bg-light">
      <tr>
        <slot name="tfoot_th"></slot>
        <td>{{ formatDecimal(prevYearTotalResults) }}</td>
        <td>{{ formatDecimal(thisYearTotalGoals) }}</td>
        <td>{{ formatDecimal(thisYearTotalResults) }}</td>
        <td class="text-center">{{ formatPercent(prevYearTotalComparison) }}</td>
        <td class="text-center">{{ formatPercent(thisYearTotalComparison) }}</td>
        <td class="text-center">{{ formatDecimal(cumulativeTotal[dates.length - 1].result) }}</td>
        <td class="text-center">{{ formatPercent(cumulativeTotalComparison(dates.length - 1)) }}</td>
        <td v-if="isMonthly"></td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import MonthlyPerformanceService from '../../../services/MonthlyPerformanceService';

export default {
  props: {
    data: {
      type: Object,
      required: true,
    },
    isMinimize: {
      type: Boolean,
      required: true,
    },
    kpiBaseValue: {
      type: Number,
      required: false,
    },
    kpiId: {
      type: Number,
      required: false,
    },
    targetId: {
      type: Number,
      required: false,
    },
    isMonthly: {
      type: Boolean,
      required: false,
    },
    locale: {
      type: String,
      required: false,
    },
    isEditable: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      performances: {},
    }
  },

  created() {
    this.initPerformances();
  },

  computed: {
    dates() {
      /**
       * PHP側ではkeyは入れた順序が保証されるが
       * 一度jsonに変換するとkeyの順序が保証されないためソートして返す
       *
       * デイリーの場合は先頭に0を含む2桁の数字なのでそのままソートをする
       *
       * マンスリーの場合は'Y年m月'形式なのでそのままソートすると以下の例で順序がおかしくなる
       * ex) 月の数字の'1'と'9'を比較するため2021年10月が先に表示されてしまう
       * 2021年10月
       * 2021年9月
       *
       * FIXME: 多言語対応で'Y年m月'形式以外が入る可能性もあるが現時点では中国語版を含めても全て'Y年m月'形式なので
       * 'Y年m月'に特化した方法でソートを行っている。将来的に対応言語が増えたときのためにより柔軟な方法に変更する
       */
      if (this.isMonthly) {
        return Object.keys(this.data).sort((a, b) => {
          const dateA = new Date(parseInt(a.slice(0, 4)), parseInt(a.slice(5, a.length - 1))).getTime();
          const dateB = new Date(parseInt(b.slice(0, 4)), parseInt(b.slice(5, b.length - 1))).getTime();
          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        });
      }
      return Object.keys(this.data).sort();
    },
    cumulativeTotal() {
      const total = new Array(this.dates.length);
      const acc = {
        goal: 0,
        result: 0,
      };
      this.dates.forEach((date, index) => {
        const { result, goal } = this.performances[date];
        acc.goal += goal ? parseFloat(goal) : 0;
        acc.result += result ? parseFloat(result) : 0;
        total[index] = {...acc};
      });
      return total;
    },
    prevYearTotalResults() {
      let result = 0;
      this.dates.forEach(date => {
        result += this.data[date]['prevYear'].result ? parseFloat(this.data[date]['prevYear'].result) : 0;
      });
      return result;
    },
    thisYearTotalGoals() {
      return this.cumulativeTotal[this.dates.length - 1].goal;
    },
    thisYearTotalResults() {
      return this.cumulativeTotal[this.dates.length - 1].result;
    },
    prevYearTotalComparison() {
      return this.calcAchievementRate(this.thisYearTotalResults, this.prevYearTotalResults, this.dates.length);
    },
    thisYearTotalComparison() {
      return this.calcAchievementRate(this.thisYearTotalResults, this.thisYearTotalGoals, this.dates.length);
    },
  },

  methods: {
    initPerformances() {
      this.dates.forEach(date => {
        this.performances[date] = {...this.data[date]['thisYear']};
      });
    },

    formatDecimal(num) {
      if (num === undefined) {
        return '';
      }
      return Math.round(num * 100) / 100;
    },

    formatPercent(num) {
      if (num === undefined) {
        return '';
      }
      return Math.round(num * 100 * 100) / 100 + ' %';
    },

    safeDivide(dividend, divisor) {
      if (!divisor || !dividend) {
        return 0;
      }
      return dividend / divisor;
    },

    /**
     * 達成率を計算する
     *
     * @param {number} result
     * @param {number} goal
     * @param {number} factor 累計目標計算時に基準値を個数倍させる
     * @returns {number}
     */
    calcAchievementRate(result, goal, factor = 1) {
      if (this.isMinimize) {
        return this.safeDivide(goal, result);
      }
      return this.safeDivide(result - this.kpiBaseValue * factor, goal - this.kpiBaseValue * factor);
    },

    prevYearComparison(date) {
      const prevYear = this.data[date]['prevYear']['result'];
      const thisYear = this.performances[date]['result'];
      if (prevYear === undefined || thisYear === undefined) {
        return undefined;
      }

      return this.calcAchievementRate(parseFloat(thisYear), parseFloat(prevYear));
    },

    thisYearComparison(date) {
      const { goal, result } = this.performances[date];
      if (goal === undefined || result === undefined) {
        return undefined;
      }

      return this.calcAchievementRate(result, goal);
    },

    cumulativeTotalComparison(to) {
      const acc = this.cumulativeTotal[to];
      const numValues = to + 1;
      return this.calcAchievementRate(acc.result, acc.goal, numValues);
    },

    async reflectDailyPerformances(month) {
      const response = await MonthlyPerformanceService.getMonthlyTotal(this.kpiId, this.targetId, month, this.locale);
      this.performances[month] = {...response.data};
    }
  }
}
</script>
